/**
 * Vuex locale 모듈
 * REQ-LOCALE-FE-008: locale 상태 단일 진입점
 * REQ-LOCALE-FE-002/003: 우선순위 체인 + enum whitelist
 * REQ-LOCALE-FE-011: document.documentElement.lang 동기 갱신
 *
 * SPEC: SPEC-CHINA-ADMIN-LOCALE-CORE-001
 */

import i18n from '@/i18n';
import { patchAccountLocale } from '@/apis/account.js';

// 지원 enum — 이 목록 외 값은 missing으로 간주
const SUPPORTED_LOCALES = ['ko', 'zh'];

/**
 * enum whitelist 검증
 * @param {*} v - 검증할 값
 * @returns {boolean}
 */
const isValid = (v) => typeof v === 'string' && SUPPORTED_LOCALES.includes(v);

// REQ-LOCALE-FE-004 강화: antLocale computed boot flash 방지
// state 초기값을 localStorage에서 동기적으로 읽어, applyLocale async dispatch 완료 이전에도
// state.locale이 올바른 값을 가지도록 보장 (App.vue ConfigProvider 첫 render frame부터 정확)
const _bootStored = localStorage.getItem('admin_china_locale');
const _bootLocale = SUPPORTED_LOCALES.includes(_bootStored) ? _bootStored : 'zh';

const state = () => ({
  // 현재 적용된 locale — localStorage 동기 읽기로 boot flash 방지 (REQ-LOCALE-FE-004 강화)
  locale: _bootLocale,
});

const mutations = {
  /**
   * locale 상태 갱신 — 단일 write 진입점
   * @param {object} state
   * @param {string} lang - 'ko' | 'zh'
   */
  SET_LOCALE(state, lang) {
    state.locale = lang;
  },
  /**
   * logout 시 state 초기화 (REQ-LOCALE-FE-010)
   * localStorage는 보존 (R-11 트레이드오프 수용)
   *
   * [HARD] 이 뮤테이션을 직접 commit하지 말 것 — 반드시 resetLocale 액션을 경유한다.
   * state만 되돌리고 i18n을 남겨두면 "헤더·사이드바=중문 / 본문=한국어" 분열 화면이 된다
   * (2026-08-13 실관측: 로그아웃 후 재로그인 세션이 분열 상태로 지속).
   */
  RESET(state) {
    state.locale = 'zh';
  },
};

const actions = {
  /**
   * 모든 locale 변경의 단일 진입점 (REQ-LOCALE-FE-008)
   *
   * source 분기:
   *   - 'login': login 응답 payload 우선순위 체인 (account > company > 'zh')
   *   - 'localStorage': localStorage backup 적용
   *   - 'dropdown' | 'info': 직접 value, PATCH 발행
   *
   * @param {object} context - Vuex context
   * @param {object} options
   * @param {string} options.source - 'login' | 'localStorage' | 'dropdown' | 'info'
   * @param {string} [options.value] - dropdown/info source에서 직접 전달
   * @param {object} [options.payload] - login source에서 { account, company } 전달
   */
  async applyLocale({ commit }, { source, value, payload } = {}) {
    let selected;

    if (source === 'login') {
      // REQ-LOCALE-FE-002/003: 우선순위 체인 + enum whitelist
      const { account, company } = payload || {};
      if (isValid(account?.locale)) {
        selected = account.locale;
      } else if (isValid(company?.locale)) {
        selected = company.locale;
      } else {
        selected = 'zh';
      }
    } else if (source === 'localStorage') {
      // REQ-LOCALE-FE-004: localStorage backup
      const stored = localStorage.getItem('admin_china_locale');
      selected = isValid(stored) ? stored : 'zh';
    } else {
      // 'dropdown' | 'info': 직접 전달값 사용
      selected = isValid(value) ? value : 'zh';
    }

    if (source === 'info') {
      // REQ-LOCALE-FE-007 / S-FE-007b: info source는 응답 대기 후 상태 적용 (낙관적 업데이트 아님)
      // 실패 시 i18n/localStorage 변경 0건 — 호출처(LocaleSection.vue)에서 toast 처리
      await patchAccountLocale(selected);
      // 성공 시에만 상태 적용
    }

    // 드롭다운/login/localStorage은 낙관적(즉시) 적용
    // info source도 PATCH 성공 후 여기로 도달
    // REQ-LOCALE-FE-001: composition mode ref .value 규약
    i18n.global.locale.value = selected;
    // REQ-LOCALE-FE-011: html lang 동기 갱신 (nextTick 전, synchronous)
    document.documentElement.lang = selected;
    // localStorage 동기화
    localStorage.setItem('admin_china_locale', selected);
    // Vuex state 갱신
    commit('SET_LOCALE', selected);

    // PATCH 발행: dropdown source만 (fire-and-forget, REQ-LOCALE-FE-005)
    if (source === 'dropdown') {
      try {
        await patchAccountLocale(selected);
      } catch (e) {
        // REQ-LOCALE-FE-006: AbortError는 toast 미표시
        if (e.name === 'AbortError' || e.code === 'ERR_CANCELED') return;
        // 기타 실패는 재throw (컴포넌트에서 toast 처리)
        throw e;
      }
    }
  },

  /**
   * logout 시 locale 초기화의 단일 진입점.
   *
   * Vuex state와 i18n·document.lang을 함께 zh로 되돌린다. RESET 뮤테이션만 커밋하면
   * i18n이 직전 언어로 남아 상태 분열(헤더=중문, 본문=이전 언어)이 생기므로,
   * applyLocale과 같은 4종 세트(단, localStorage는 R-11에 따라 보존)로 처리한다.
   */
  resetLocale({ commit }) {
    i18n.global.locale.value = 'zh';
    document.documentElement.lang = 'zh';
    commit('RESET');
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
